import React, { useEffect, useRef } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useUserProfile } from '../providers/UserProfileProvider';
import { useAuth } from '../../Authentication/components/RealTimeAuthStatus';

/**
 * Component that handles user identification for analytics
 * Must be rendered inside UserProfileProvider and AuthProvider
 */
export const AnalyticsIdentifier: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const analytics = useAnalytics();
  const { userProfile } = useUserProfile();
  const { session } = useAuth();
  const hasResetRef = useRef(false);
  const lastSessionIdRef = useRef<string | null>(null);

  // Identify user when profile loads
  useEffect(() => {
    if (userProfile) {
      analytics.identifyUser();
    }
  }, [userProfile, analytics]);

  // Reset analytics on logout (only once per logout)
  useEffect(() => {
    const sessionId = session?.user?.id || null;
    
    // If we had a session and now we don't, reset analytics
    if (lastSessionIdRef.current && !sessionId && !hasResetRef.current) {
      analytics.resetAtLogout();
      hasResetRef.current = true;
    }
    
    // If we have a new session, reset the flag
    if (sessionId && sessionId !== lastSessionIdRef.current) {
      hasResetRef.current = false;
    }
    
    lastSessionIdRef.current = sessionId;
  }, [session, analytics]);

  return <>{children}</>;
};